import { Controller } from "@hotwired/stimulus"

// Shared header behavior for Apps, Pinned and Recent. Collapse persistence
// stays with each section's existing controller.
export default class extends Controller {
    static targets = ["filterPanel", "filterToggle", "filterInput", "filterEmpty"]
    static values = { rows: String, remote: Boolean, hideEmpty: Boolean }

    connect() {
        if (!this.hideEmptyValue) return
        this.observer = new MutationObserver(() => this.updateVisibility())
        this.observer.observe(this.element.querySelector(".rail-chats-list"), { childList: true, subtree: true })
        this.updateVisibility()
    }

    disconnect() { this.observer?.disconnect() }

    updateVisibility() {
        if (!this.hideEmptyValue) return
        const searching = !!this.filterInputTarget.value.trim()
        this.element.hidden = !searching && !this.element.querySelector(".rail-chat, .rail-pin-group, .rail-chat-project-group")
    }

    collapse() {
        const toggle = this.element.querySelector(".rail-chats-heading")
        if (toggle.getAttribute("aria-expanded") === "true") toggle.click()
    }

    toggleFilter() {
        const open = this.filterPanelTarget.hidden
        this.filterPanelTarget.hidden = !open
        this.filterToggleTarget.setAttribute("aria-expanded", String(open))
        if (open) {
            const toggle = this.element.querySelector(".rail-chats-heading")
            if (toggle.getAttribute("aria-expanded") === "false") toggle.click()
            this.filterInputTarget.focus()
        } else {
            this.filterInputTarget.value = ""
            this.filter()
        }
    }

    filter() {
        this.element.dispatchEvent(new Event("sidebar:filter", { bubbles: true }))
        const query = this.filterInputTarget.value.trim().toLocaleLowerCase()
        if (this.remoteValue) {
            this.element.dispatchEvent(new CustomEvent("sidebar:query", { detail: { query } }))
            return
        }
        this.updateVisibility()
        this.element.classList.toggle("is-filtering", !!query)
        const rows = [...this.element.querySelectorAll(this.rowsValue)]
        const folders = [...this.element.querySelectorAll(".rail-pin-folder")]
        const folderMatches = folder => folder?.querySelector(":scope > summary .rail-chat-label").textContent.toLocaleLowerCase().includes(query)
        const ancestorMatches = folder => {
            while (folder) {
                if (folderMatches(folder)) return true
                folder = folder.parentElement.closest(".rail-pin-folder")
            }
            return false
        }
        rows.forEach(row => {
            const matches = row.textContent.toLocaleLowerCase().includes(query) || ancestorMatches(row.closest(".rail-pin-folder"))
            row.classList.toggle("is-section-filtered", !matches)
        })
        folders.forEach(folder => {
            const matches = ancestorMatches(folder) || [...folder.querySelectorAll(".rail-pin-folder")].some(folderMatches) ||
                [...folder.querySelectorAll(this.rowsValue)].some(row => !row.classList.contains("is-section-filtered"))
            folder.closest(".rail-pin-group").classList.toggle("is-section-filtered", !matches)
            if (query) {
                if (!folder.hasAttribute("data-before-filter-open")) folder.dataset.beforeFilterOpen = String(folder.open)
                folder.open = matches
            } else if (folder.hasAttribute("data-before-filter-open")) {
                folder.open = folder.dataset.beforeFilterOpen === "true"
                delete folder.dataset.beforeFilterOpen
            }
        })
        this.filterEmptyTarget.hidden = !query || rows.some(row => !row.classList.contains("is-section-filtered")) || folders.some(folder => folderMatches(folder))
        this.element.querySelectorAll(".rail-chats-list > .rail-chats-empty").forEach(empty => { empty.hidden = !!query })
    }

    filterKeydown(event) {
        if (event.key !== "Escape") return
        event.preventDefault()
        event.stopPropagation()
        this.toggleFilter()
        this.filterToggleTarget.focus()
    }
}
